import { useState } from 'react';
import {
  useBuyPhoneNumberMutation,
  useGetPhoneNumbersQuery,
  useLazySearchPhoneNumbersQuery
} from '@/store/configApi';
import { useActiveLocation } from '@/hooks/useActiveLocation';
import { useAppDispatch } from '@/store';
import { pushToast } from '@/store/uiSlice';
import { Button, EmptyState, Panel, SkeletonRows, Status } from '@/components/ui';
import { formatDateTime, formatMoney, formatPhone } from '@/utils/format';

export function PhonePage() {
  const { locationId, locations } = useActiveLocation();
  const dispatch = useAppDispatch();

  const numbers = useGetPhoneNumbersQuery();
  const [search, searchState] = useLazySearchPhoneNumbersQuery();
  const [buy, buyState] = useBuyPhoneNumberMutation();
  const [areaCode, setAreaCode] = useState('');

  const claim = async (phoneNumber: string) => {
    const result = await buy({ phoneNumber, locationId });
    dispatch(
      'error' in result
        ? pushToast('Could not set up that number.', 'error')
        : pushToast(`${formatPhone(phoneNumber)} is ready to take calls.`, 'success')
    );
  };

  const locationName = (id?: string) =>
    locations.find((location) => location._id === id)?.name ?? 'Not assigned';

  return (
    <div className="space-y-4">
      <h1 className="text-display">Phone numbers</h1>

      <Panel title="Your numbers">
        {numbers.isLoading ? (
          <SkeletonRows rows={3} />
        ) : (numbers.data?.data.length ?? 0) === 0 ? (
          <EmptyState
            headline="No numbers yet"
            hint="Pick a number below. Then forward your existing restaurant number to it, so customers keep dialling the number they know."
          />
        ) : (
          <ul className="divide-y divide-rule">
            {numbers.data?.data.map((number) => (
              <li key={number._id} className="flex flex-wrap items-center gap-3 px-4 py-3.5">
                <span className="tabular text-small text-ink">
                  {formatPhone(number.number)}
                </span>
                <span className="text-small text-slate">{locationName(number.locationId)}</span>
                <Status value={number.status} />

                <span className="ml-auto text-small text-slate">
                  {number.webhookHealthy ? (
                    number.lastWebhookAt ? (
                      <>Last call {formatDateTime(number.lastWebhookAt)}</>
                    ) : (
                      'Waiting for its first call'
                    )
                  ) : (
                    <span className="text-rust">Not receiving calls</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel title="Add a number">
        <div className="px-4 py-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-40 space-y-1.5">
              <label htmlFor="areaCode" className="block text-small font-medium text-ink">
                Area code
              </label>
              <input
                id="areaCode"
                value={areaCode}
                onChange={(event) => setAreaCode(event.target.value)}
                placeholder="416"
                className="w-full rounded border border-rule bg-white px-3 py-2 text-small"
              />
            </div>
            <Button
              variant="secondary"
              loading={searchState.isFetching}
              onClick={() => search({ areaCode: areaCode || undefined, limit: 10 })}
            >
              Find numbers
            </Button>
          </div>

          {searchState.isError ? (
            <p className="mt-4 rounded bg-rust-soft px-3 py-2 text-small text-rust">
              Number search is unavailable. Check that your Telnyx key is set on the server.
            </p>
          ) : null}

          {searchState.data ? (
            searchState.data.data.length === 0 ? (
              <p className="mt-4 text-small text-slate">
                Nothing free in that area code. Try a nearby one.
              </p>
            ) : (
              <ul className="mt-4 divide-y divide-rule border-y border-rule">
                {searchState.data.data.map((number) => (
                  <li key={number.phoneNumber} className="flex items-center gap-3 py-2.5">
                    <span className="tabular text-small text-ink">
                      {formatPhone(number.phoneNumber)}
                    </span>
                    <span className="text-small text-slate">{number.locality ?? ''}</span>
                    <span className="tabular ml-auto text-small text-slate">
                      {formatMoney(number.monthlyCostCents)}/month
                    </span>
                    <Button
                      loading={buyState.isLoading}
                      disabled={!locationId}
                      onClick={() => claim(number.phoneNumber)}
                    >
                      Use this
                    </Button>
                  </li>
                ))}
              </ul>
            )
          ) : null}
        </div>
      </Panel>

      <Panel title="Forwarding your existing number">
        <div className="px-4 py-4 text-small text-slate">
          <p>
            Keep the number on your signage. Ask your phone provider to forward it to the
            number above, and set it to forward when the line is busy or unanswered if you
            want staff to pick up first.
          </p>
        </div>
      </Panel>
    </div>
  );
}
